// ---------------------------------------------------------------------------
// Past Tour index (`/past-tour`).
// Renders the most-recent completed tour as a minimal, focused list of its
// events. Intentionally no video hero, info cards, quick links, hotel details,
// or registration CTAs — just a text header and the event list.
//
// The `?type=past` feed embeds per-event availability flags server-side, so
// this route needs no N+1 per-event status fetches (unlike `/tour`).
//
// NOTE: The feed returns raw VOD URLs (`wrapItVodUrl` / `danceAgainVodUrl`).
// We only use them to derive the Watch availability flag — the Watch link
// always routes through `/past-tour/{slug}/stream` and never links a raw VOD
// URL (raw deep-linking is out of scope per the PRD).
// ---------------------------------------------------------------------------

import { Link, useLoaderData } from 'react-router'
import { getTourDates } from '#app/utils/dmapi.server.ts'
import { type Route } from './+types/past-tour._index.ts'

type TourEvent = {
	id: string
	name: string
	slug: string
	dates: string
	startDate: string | null
	endDate: string | null
	venue: string | null
	// Past-only fields (see the locked contract in issue #2):
	showWinners: boolean
	wrapItVodUrl: string | null
	danceAgainVodUrl: string | null
}

type TourData = {
	tour: { id: string; name: string; years: string } | null
	events: TourEvent[]
}

export const meta: Route.MetaFunction = () => {
	const title = 'Past Tour | DanceMakers'
	const description =
		'Relive our most-recent completed DanceMakers tour — browse the events, watch the recap videos, and view the competition results.'
	const siteUrl = 'https://dancemakersinc.com'

	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: `${siteUrl}/past-tour` },
	]
}

export async function loader() {
	const apiUrl = process.env.DMAPI_URL || 'http://localhost:3001'

	try {
		const tourData = await getTourDates<TourData>(apiUrl, 'past')

		return { tourData }
	} catch (error) {
		console.error('Error fetching past tour dates:', error)
		return { tourData: null as TourData | null }
	}
}

// Format event date range using UTC to avoid timezone issues
function formatEventDates(event: TourEvent): string {
	if (!event.startDate) return event.dates // Fall back to pre-formatted string if no startDate

	const startDate = new Date(event.startDate)
	const endDate = event.endDate ? new Date(event.endDate) : null

	const startMonth = startDate.toLocaleDateString('en-US', {
		month: 'short',
		timeZone: 'UTC',
	})
	const startDay = startDate.toLocaleDateString('en-US', {
		day: 'numeric',
		timeZone: 'UTC',
	})
	// A season spans two calendar years, so show each event's year. Use the end
	// date's year for cross-year events (e.g. a Dec 30 - Jan 1 event reads 2025).
	const year = (endDate ?? startDate).toLocaleDateString('en-US', {
		year: 'numeric',
		timeZone: 'UTC',
	})

	if (!endDate) return `${startMonth} ${startDay}, ${year}`

	const endMonth = endDate.toLocaleDateString('en-US', {
		month: 'short',
		timeZone: 'UTC',
	})
	const endDay = endDate.toLocaleDateString('en-US', {
		day: 'numeric',
		timeZone: 'UTC',
	})

	// Same month: "Nov 8-10, 2024"
	if (startMonth === endMonth)
		return `${startMonth} ${startDay}-${endDay}, ${year}`

	// Different months: "Nov 30 - Dec 1, 2024"
	return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`
}

export default function PastTourIndex() {
	const { tourData } = useLoaderData<typeof loader>()
	const events = tourData?.events ?? []
	const tourName = tourData?.tour?.name
	const tourYears = tourData?.tour?.years

	return (
		<div className="min-h-screen pb-16 pt-32">
			<section className="container px-6 md:px-16">
				<p className="text-xs font-medium uppercase tracking-[0.2em] text-primary md:text-sm">
					Past Tour
				</p>
				<h1 className="mb-3 font-display text-2xl font-bold uppercase md:text-4xl">
					{tourYears && tourName ? `${tourYears} ${tourName}` : 'Past Tour'}
				</h1>
				<p className="max-w-xl text-sm text-muted-foreground">
					Look back on our most-recent completed tour. Watch the recap and view
					results where available.
				</p>
			</section>

			<section className="container px-6 py-12 md:px-16">
				{events.length > 0 ? (
					<div className="grid gap-4">
						{events.map((event, index) => {
							const hasWatch = Boolean(
								event.wrapItVodUrl || event.danceAgainVodUrl,
							)
							const hasResults = event.showWinners

							return (
								<div
									key={event.id}
									className="group flex flex-col justify-between border border-border/50 bg-card/50 p-6 transition-all duration-300 hover:border-primary/50 hover:bg-card md:flex-row md:items-center"
									style={{ animationDelay: `${index * 0.05}s` }}
								>
									<div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-12">
										<span className="min-w-[120px] text-sm font-bold uppercase tracking-wider text-primary">
											{formatEventDates(event)}
										</span>
										<span className="font-display text-xl font-bold uppercase md:text-2xl">
											{event.name}
										</span>
										{event.venue && (
											<span className="text-sm text-muted-foreground">
												{event.venue}
											</span>
										)}
									</div>
									{(hasWatch || hasResults) && (
										<div className="mt-4 flex items-center gap-6 md:mt-0">
											{hasWatch && (
												<Link
													to={`/past-tour/${event.slug}/stream`}
													className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:text-primary/80"
												>
													Watch
													<svg
														className="h-4 w-4"
														fill="none"
														viewBox="0 0 24 24"
														stroke="currentColor"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
														/>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
														/>
													</svg>
												</Link>
											)}
											{hasResults && (
												<Link
													to={`/past-tour/${event.slug}/results`}
													className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:text-primary/80"
												>
													Results
													<svg
														className="h-4 w-4"
														fill="none"
														viewBox="0 0 24 24"
														stroke="currentColor"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M9 5l7 7-7 7"
														/>
													</svg>
												</Link>
											)}
										</div>
									)}
								</div>
							)
						})}
					</div>
				) : (
					<div className="py-24 text-center">
						<h2
							className="mb-4 text-xl font-bold uppercase sm:text-2xl"
							style={{ fontFamily: 'var(--font-display)' }}
						>
							No Past Tour Available
						</h2>
						<p className="text-muted-foreground">
							Check back after our current tour wraps up.
						</p>
					</div>
				)}
			</section>
		</div>
	)
}
